import { useCallback, useSyncExternalStore } from 'react'
import { IconButton } from './primitives.js'

export type ThemeChoice = 'light' | 'dark'
export const THEME_KEY = 'algoboo.theme'

const listeners = new Set<() => void>()

export function currentTheme(): ThemeChoice {
  if (typeof document === 'undefined') return 'dark'
  return document.documentElement.dataset['theme'] === 'light' ? 'light' : 'dark'
}

export function setTheme(t: ThemeChoice): void {
  document.documentElement.dataset['theme'] = t
  document.documentElement.style.colorScheme = t
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t === 'light' ? '#fafafa' : '#0a0a0b')
  try {
    localStorage.setItem(THEME_KEY, t)
  } catch {
    // storage blocked: theme still applies for this page
  }
  listeners.forEach((l) => l())
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useTheme(): [ThemeChoice, (t: ThemeChoice) => void] {
  const t = useSyncExternalStore(subscribe, currentTheme, () => 'dark' as const)
  return [t, useCallback((n: ThemeChoice) => setTheme(n), [])]
}

export function ThemeToggle({ className }: { className?: string }) {
  const [t, set] = useTheme()
  const next = t === 'dark' ? 'light' : 'dark'
  return <IconButton icon={t === 'dark' ? 'sun' : 'moon'} label={`Switch to ${next} theme`} onClick={() => set(next)} {...(className ? { className } : {})} />
}
